import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import { socket } from "../../../../../../Socket/socket";
import { addCamectAPI, getCamectCameras, getDatabaseCamectIntegrations, linkCamectCameras, camectConnectionTest, updateCamectAPI, updateCamectRelationshipAPI } from "../../../../../../Api Methods/Api";
import classes from "../../RuleWizard/RuleWizard.module.css";
import WizardStepCamectControlType from "./WizardStepCamectControlType";
import WizardStepCamectForm from "./WizardStepCamectForm";
import WizardStepCamectPingTest from "./WizardStepCamectPingTest";
import WizardStepCamectOutput from "./WizardStepCamectOutput";
import WizardStepCamectCameraList from "./WizardStepCamectCameraList";
import WizardStepCamectResult from "./WizardStepCamectResult";
import { add } from "date-fns";

// Camect setup wizard - laid out like the HikvisionWizard (step sidebar + content pane).
// Flow: Control type -> Camect details -> Connection test -> [Site output] -> [Cameras] -> [Live view cameras] -> Finish
// Site output only appears when "Control" was picked; Cameras only when control is picked and the site's
// camectIntegrationType is 2 (per camera arming) - type 1 arms the whole Camect from the output.
// Live view cameras only appears when "Live view" was picked.
// The Camect is saved once the connection test passes, as its ID is needed to list its cameras.

const CamectWizard = ({ setShowVirtualSource, outputs, camectIntegrationType, fetchVirtualSources, liveViewEnabled }) => {
    const dispatch = useDispatch();
    const client = useSelector(state => state.auth.client);
    const token = useSelector(state => state.auth.token);
    const DeviceUUID = useSelector(state => state.auth.DeviceUUID);
    const apiData = { token, client };

    const [currentStep, setCurrentStep] = useState("controlType");
    const [completedSteps, setCompletedSteps] = useState(new Set());

    const [selectedOptions, setSelectedOptions] = useState([]);
    const [camectDetails, setCamectDetails] = useState({ address: "", password: "" });
    const [connectionError, setConnectionError] = useState("");
    // testFailed shows the "Continue without connection" option; connectionSkipped is set once it's used
    const [testFailed, setTestFailed] = useState(false);
    const [connectionSkipped, setConnectionSkipped] = useState(false);
    const [liveViewSkipped, setLiveViewSkipped] = useState(false);
    // Set once the Camect has been saved - re-testing after going back updates it instead of adding a duplicate
    const [camectID, setCamectID] = useState(null);
    const [cameras, setCameras] = useState([]);

    const [pinSelected, setPinSelected] = useState(null);
    const [selectedCameraIds, setSelectedCameraIds] = useState([]);
    const [selectedLiveViewCameraIds, setSelectedLiveViewCameraIds] = useState([]);

    const [linkStatus, setLinkStatus] = useState("linking");
    const [failReason, setFailReason] = useState("");

    const wantsControl = selectedOptions.includes("Control");
    const wantsLiveView = selectedOptions.includes("Live view");
    const isPerCameraArming = camectIntegrationType == 2;
    // Without a connection the Camect's cameras can't be listed, so the camera steps are dropped.
    // Per camera arming links outputs by picking cameras, so with the connection skipped it can't link an
    // output at all - the Camect is only added. Whole hub mode still links the output to the whole Camect.
    const linksOutput = (skipped) => wantsControl && !(isPerCameraArming && skipped);
    const picksCameras = wantsControl && isPerCameraArming && !connectionSkipped;
    const controlNotLinked = wantsControl && !linksOutput(connectionSkipped);

    const buildStepConfigs = (skipped) => [
        { step: "controlType", label: "Control type" },
        { step: "form", label: "Camect details" },
        { step: "pingTest", label: "Connection test" },
        linksOutput(skipped) && { step: "output", label: "Site output" },
        wantsControl && isPerCameraArming && !skipped && { step: "cameras", label: "Cameras" },
        wantsLiveView && !skipped && { step: "liveView", label: "Live view cameras" },
        { step: "result", label: "Finish" },
    ].filter(Boolean);

    const stepConfigs = buildStepConfigs(connectionSkipped);

    const stepOrder = stepConfigs.map(c => c.step);
    // Last step the user configures before linking - its Next button becomes "Link Camect"
    const lastSelectionStep = stepOrder[stepOrder.length - 2];

    const goTo = (step) => {
        setCompletedSteps(prev => new Set(prev).add(currentStep));
        setCurrentStep(step);
    };

    const nextStep = (from) => stepOrder[stepOrder.indexOf(from) + 1];
    // "Connection test" is transient, so going back skips over it to the details form
    const prevStep = (from) => {
        const prev = stepOrder[stepOrder.indexOf(from) - 1];
        return prev === "pingTest" ? "form" : prev;
    };

    const HandleClose = () => setShowVirtualSource(null);

    const testCamectConnection = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15_000);

        try {
            const body = {
                camectURL: camectDetails.address.trim(),
                username: "admin",
                password: camectDetails.password,
            };
            const response = await camectConnectionTest(apiData, body, dispatch, controller.signal);
            console.log(response);
            return response;
        } catch (err) {
            console.log(err);
            return {
                success: false,
                message: err.name === "AbortError"
                    ? "Connection to the Camect timed out. Please check the address and try again."
                    : "Failed to test the Camect connection, please try again",
            };
        } finally {
            clearTimeout(timeoutId);
        }
    };

    // addCamectAPI doesn't hand back the new ID - it arrives on the 'appCamects' socket message
    // ({ uuid, camects: [{ id, address, ... }] }) once the Camect has been added.
    // Resolves with the ID of the Camect matching this site & address, or null if nothing arrives in time.
    const waitForCamectID = (address) => {
        console.log(address);
        let onAppCamects;
        let timeoutId;

        const promise = new Promise(resolve => {
            onAppCamects = (value) => {
                console.log(value);
                if (value?.uuid !== DeviceUUID) return;
                const camects = value.camects ?? [];
                const matches = camects.filter(camect => camect.address === address);
                if (matches.length === 0) return;
                // the same address can be added more than once (e.g. retrying the wizard) - the newest is last
                resolve(matches[matches.length - 1].id);
            };

            
            timeoutId = setTimeout(() => resolve(null), 15_000);
            socket.on("appCamects", onAppCamects);
        });

        // only removes this listener - App.js's own appCamects handler is left alone
        const cleanup = () => {
            clearTimeout(timeoutId);
            socket.off("appCamects", onAppCamects);
        };

        return { promise: promise.finally(cleanup), cleanup };
    };

    const saveCamect = async () => {
        const address = camectDetails.address.trim();
        const body = {
            uuid: DeviceUUID,
            address,
            username: "admin",
            password: camectDetails.password,
        };

        // if (camectID) {
        //     await updateCamectAPI(apiData, { id: camectID, ...body }, dispatch);
        //     return camectID;
        // }

        // listen before adding so the socket message can't arrive before we're subscribed
        const camectIDListener = waitForCamectID(address);
        try {
            await addCamectAPI(apiData, body, dispatch);
            fetchVirtualSources()
        } catch (err) {
            camectIDListener.cleanup();
            console.log(err);
            throw err;
        }
        return camectIDListener.promise;
    };

    // A Camect that's only just been added often returns no cameras until the backend has connected to it,
    // so retry a few times while the list comes back empty
    const fetchCamectCameras = async (savedCamectID, attempts = 4, delayMs = 3000) => {
        for (let attempt = 1; attempt <= attempts; attempt++) {
            try {
                const response = await getCamectCameras(apiData, DeviceUUID, savedCamectID);
                if (Array.isArray(response?.cameras) && response.cameras.length > 0) return response.cameras;
            } catch (err) {
                console.log(err);
            }
            if (attempt < attempts) await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        return [];
    };

    // skipTest - "Continue without connection" after a failed test: the Camect is still added & linked
    // to an output, but the camera steps are skipped as its cameras can't be listed
    const HandleTestConnection = async (skipTest = false) => {
        setConnectionError("");
        goTo("pingTest");

        try {
            if (!skipTest) {
                const response = await testCamectConnection();
                console.log(response);
                if (!response?.success) {
                    setTestFailed(true);
                    setConnectionError(response?.message || "Failed to connect to the Camect, please check the details & try again");
                    setCurrentStep("form");
                    return;
                }
            }

            const savedCamectID = await saveCamect();
            if (!savedCamectID) {
                setConnectionError(skipTest
                    ? "The Camect couldn't be confirmed as added to this site. Please try again"
                    : "The Camect connected, but it couldn't be confirmed as added to this site. Please try again");
                setCurrentStep("form");
                return;
            }
            setCamectID(savedCamectID);
            setConnectionSkipped(skipTest);
            setTestFailed(false);
            //fetchVirtualSources();

            if (!skipTest) {
                // fetched regardless of control type, in case it's changed by going back
                setCameras(await fetchCamectCameras(savedCamectID));
            }

            setCompletedSteps(prev => new Set(prev).add("form").add("pingTest"));

            // stepOrder from this render doesn't reflect the skip yet, so work out the next step from the new state
            const newStepOrder = buildStepConfigs(skipTest).map(c => c.step);
            const next = newStepOrder[newStepOrder.indexOf("pingTest") + 1];
            // Live view only with the connection skipped leaves nothing to pick - just finish
            if (next === "result") {
                HandleLink({ skipped: skipTest });
            } else {
                setCurrentStep(next);
            }
        } catch {
            setConnectionError("Failed to connect to the Camect, please check the details & try again");
            setCurrentStep("form");
        }
    };

    // skipped is passed in when called straight after the connection step, before connectionSkipped has updated.
    // skipLiveView - "Skip & link Camect" on the live view step when the Camect returned no cameras
    const HandleLink = async ({ skipped = connectionSkipped, skipLiveView = false } = {}) => {
        goTo("result");
        setLinkStatus("linking");
        setLiveViewSkipped(skipLiveView);

        try {
            if (linksOutput(skipped)) {
                // Whole Camect links as "<camectID>", per camera arming as "<camectID>:<camID>;<camID>"
                const body = {
                    uuid: DeviceUUID,
                    id: picksCameras ? `${camectID}:${selectedCameraIds.join(";")}` : camectID,
                    pin: String(pinSelected),
                };

                const linkResponse = await updateCamectRelationshipAPI(apiData, body, dispatch);
                if (!linkResponse || linkResponse.success === false) {
                    setFailReason(linkResponse?.message || "Failed to link the Camect to the output, please try again.");
                    setLinkStatus("failed");
                    return;
                }
            }

            if (wantsLiveView && !skipped && !skipLiveView) {
                // Live view cameras are linked against the Camect's integration record, not the Camect ID
                const integrationsResponse = await getDatabaseCamectIntegrations(apiData, DeviceUUID, dispatch);
                const integrationList = integrationsResponse?.integrationRelationships ?? integrationsResponse?.data ?? integrationsResponse;
                const integration = Array.isArray(integrationList)
                    ? integrationList.find(entry => entry.camect_id == camectID)
                    : null;

                if (!integration) {
                    fetchVirtualSources();
                    setFailReason(`${wantsControl ? "The Camect was linked, but the" : "The"} live view cameras could not be added as the Camect integration wasn't found.`);
                    setLinkStatus("failed");
                    return;
                }

                const body = {
                    siteUUID: DeviceUUID,
                    integrationId: integration.id,
                    cameras: cameras
                        .filter(camera => selectedLiveViewCameraIds.includes(camera.id))
                        .map(camera => ({
                            name: camera.name,
                            cameraId: camera.id,
                            type: "CAMECT",
                            defaultPinned: 0,
                        })),
                };

                const liveViewResponse = await linkCamectCameras(apiData, body, dispatch);
                if (!liveViewResponse?.success) {
                    fetchVirtualSources();
                    setFailReason(`${wantsControl ? "The Camect was linked, but the" : "The"} live view cameras could not be added.`);
                    setLinkStatus("failed");
                    return;
                }
            }

            fetchVirtualSources();
            setLinkStatus("success");
        } catch {
            setFailReason("An unexpected error occurred. Please try again.");
            setLinkStatus("failed");
        }
    };

    const advanceFrom = (step) => step === lastSelectionStep ? HandleLink() : goTo(nextStep(step));

    // Explains on the form what skipping the connection test leaves out, and on Finish what wasn't set up
    const skippedParts = [
        wantsControl && isPerCameraArming && "linking cameras to outputs",
        wantsLiveView && "adding live view cameras",
    ].filter(Boolean);
    const skipNote = skippedParts.length > 0
        ? `Continuing without a connection still adds the Camect, but ${skippedParts.join(" & ")} will be skipped`
        : "Continuing without a connection still adds & links the Camect";
    const resultNote = connectionSkipped && skippedParts.length > 0
        ? `As the Camect couldn't be reached, ${skippedParts.join(" & ")} was skipped. This can be done from the Camect once it's reachable.`
        : liveViewSkipped
            ? "No cameras were found on the Camect, so live view was skipped. Live view cameras can be added later once cameras are available."
            : null;

    const getStepSummary = (step) => {
        switch (step) {
            case "controlType":
                return selectedOptions.join(" + ") || null;
            case "form":
                return camectDetails.address || null;
            case "pingTest":
                return connectionSkipped ? "Skipped" : "Connected";
            case "output":
                return outputs.find(o => o.Pin === pinSelected)?.pinName ?? null;
            case "cameras":
                return `${selectedCameraIds.length} camera${selectedCameraIds.length === 1 ? "" : "s"}`;
            case "liveView":
                return `${selectedLiveViewCameraIds.length} camera${selectedLiveViewCameraIds.length === 1 ? "" : "s"}`;
            default:
                return null;
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case "controlType":
                return (
                    <WizardStepCamectControlType
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        liveViewDisabled={!isPerCameraArming}
                        onNext={() => goTo(nextStep("controlType"))}
                        liveViewPermitted={liveViewEnabled}
                    />
                );
            case "form":
                return (
                    <WizardStepCamectForm
                        camectDetails={camectDetails}
                        setCamectDetails={setCamectDetails}
                        connectionError={connectionError}
                        onNext={() => HandleTestConnection()}
                        onSkip={testFailed ? () => HandleTestConnection(true) : undefined}
                        skipNote={skipNote}
                        onBack={() => setCurrentStep(prevStep("form"))}
                    />
                );
            case "pingTest":
                return <WizardStepCamectPingTest onClose={HandleClose} />;
            case "output":
                return (
                    <WizardStepCamectOutput
                        outputs={outputs}
                        pinSelected={pinSelected}
                        setPinSelected={setPinSelected}
                        isLastStep={lastSelectionStep === "output"}
                        onNext={() => advanceFrom("output")}
                        onBack={() => setCurrentStep(prevStep("output"))}
                    />
                );
            case "cameras":
                return (
                    <WizardStepCamectCameraList
                        title="Which cameras would you like to control?"
                        subtitle="The selected output will arm & disarm these cameras"
                        cameras={cameras}
                        selectedCameraIds={selectedCameraIds}
                        setSelectedCameraIds={setSelectedCameraIds}
                        isLastStep={lastSelectionStep === "cameras"}
                        onNext={() => advanceFrom("cameras")}
                        onBack={() => setCurrentStep(prevStep("cameras"))}
                    />
                );
            case "liveView":
                return (
                    <WizardStepCamectCameraList
                        title="Which cameras would you like to stream?"
                        subtitle="Selected cameras will be added as live view cameras"
                        cameras={cameras}
                        selectedCameraIds={selectedLiveViewCameraIds}
                        setSelectedCameraIds={setSelectedLiveViewCameraIds}
                        emptySkipLabel="Skip & link Camect"
                        onEmptySkip={() => HandleLink({ skipLiveView: true })}
                        isLastStep={lastSelectionStep === "liveView"}
                        onNext={() => advanceFrom("liveView")}
                        onBack={() => setCurrentStep(prevStep("liveView"))}
                    />
                );
            case "result":
                return (
                    <WizardStepCamectResult
                        status={linkStatus}
                        failReason={failReason}
                        successTitle={controlNotLinked || (connectionSkipped && !wantsControl) ? "Camect added successfully!" : undefined}
                        note={resultNote}
                        // with nothing to pick after the connection step, go back to the details form rather than the spinner
                        onBack={() => setCurrentStep(lastSelectionStep === "pingTest" ? "form" : lastSelectionStep)}
                        onClose={HandleClose}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            visible
            onHide={HandleClose}
            style={{ width: "900px" }}
            header="Link Camect"
            modal
            pt={{ content: { style: { padding: 0 } } }}
        >
            <div className={classes.wizardContainer}>
                <div className={classes.sidebar}>
                    <span className={classes.sidebarTitle}>Camect Setup</span>
                    {stepConfigs.map((config, i) => {
                        const isActive = config.step === currentStep;
                        const isDone = completedSteps.has(config.step) && !isActive;
                        const summary = isDone ? getStepSummary(config.step) : null;
                        return (
                            <div
                                key={config.step}
                                className={`${classes.stepItem} ${isActive ? classes.stepItemActive : ""}`}
                            >
                                <div
                                    className={`${classes.stepNumber} ${
                                        isActive ? classes.stepNumberActive : isDone ? classes.stepNumberDone : ""
                                    }`}
                                >
                                    {isDone ? "✓" : i + 1}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                    <span
                                        className={`${classes.stepLabel} ${
                                            isActive ? classes.stepLabelActive : isDone ? classes.stepLabelDone : ""
                                        }`}
                                    >
                                        {config.label}
                                    </span>
                                    {summary && <span className={classes.stepSummary}>{summary}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className={classes.content}>
                    {renderStep()}
                </div>
            </div>
        </Dialog>
    );
};

export default CamectWizard;
